/**
 * Server-status overlay.
 *
 * A full-screen, themed takeover shown when the API can't be reached or is in
 * planned maintenance — so players see a clear "the realm is briefly away"
 * message instead of broken/blank pages or raw fetch errors.
 *
 * Two modes:
 *   - 'down'        — the client couldn't reach the API at all (server crash,
 *                     Fly cold-start, network drop). Auto-retries.
 *   - 'maintenance' — the API answered with a 503 MAINTENANCE signal (the
 *                     operator flipped MAINTENANCE_MODE on). Auto-retries.
 *
 * In both modes we poll GET /api/health every few seconds. Health stays up
 * during maintenance and reports `{ maintenance: boolean }`, so once the realm
 * is healthy AND not in maintenance we reload the page to re-bootstrap cleanly.
 */

const ENV_BASE = (import.meta as ImportMeta & { env?: { VITE_API_BASE_URL?: string } }).env
  ?.VITE_API_BASE_URL;
const BASE = ENV_BASE && ENV_BASE.length > 0 ? ENV_BASE.replace(/\/$/, '') : '/api';

type Mode = 'down' | 'maintenance';

let overlayEl: HTMLElement | null = null;
let pollTimer: number | null = null;
let currentMode: Mode | null = null;
let currentMessage = '';

const POLL_MS = 5000;

const DOWN_TITLE = 'The realm is beyond reach';
const DOWN_BODY =
  "We can't reach the realm just now — it may be catching its breath. " +
  'This page will reconnect on its own the moment it returns.';
const MAINT_TITLE = 'Closed for upkeep';
const MAINT_BODY_DEFAULT =
  'The realm is closed for upkeep. The gates will reopen shortly — this page will let you back in automatically.';

function ensureOverlay(): HTMLElement {
  if (overlayEl) return overlayEl;
  const el = document.createElement('div');
  el.id = 'server-status-overlay';
  el.setAttribute('role', 'alertdialog');
  el.setAttribute('aria-live', 'assertive');
  el.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:99999',
    'display:none',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    'background:#f3e9d2',
    'background-image:radial-gradient(rgba(120,90,40,0.10) 1px, transparent 1px)',
    'background-size:22px 22px',
    'color:#3a2c18',
    "font-family:Georgia,'Times New Roman',serif",
    'text-align:center',
  ].join(';');
  el.innerHTML = `
    <div style="max-width:30rem;">
      <div id="ss-crest" style="font-size:3rem; line-height:1; margin-bottom:1rem;">⚜</div>
      <h1 id="ss-title" style="font-size:1.6rem; margin:0 0 0.75rem 0; color:#5a1f1f;"></h1>
      <p id="ss-body" style="font-size:1rem; line-height:1.5; margin:0 0 1.25rem 0; color:#5a4630;"></p>
      <button id="ss-retry" style="
        font-family:inherit; font-size:0.95rem; cursor:pointer;
        padding:0.55rem 1.4rem; border-radius:6px;
        border:1px solid #7a5a2a; background:#caa44a; color:#2a1d08;">
        Try now
      </button>
      <p id="ss-status" style="font-size:0.82rem; color:#8a7350; margin:0.9rem 0 0 0;">
        Reconnecting automatically…
      </p>
    </div>`;
  document.body.appendChild(el);
  el.querySelector<HTMLButtonElement>('#ss-retry')?.addEventListener('click', () => {
    void checkHealth();
  });
  overlayEl = el;
  return el;
}

function paint(mode: Mode, message?: string): void {
  const el = ensureOverlay();
  const title = el.querySelector('#ss-title');
  const body = el.querySelector('#ss-body');
  const crest = el.querySelector('#ss-crest');
  if (title) title.textContent = mode === 'maintenance' ? MAINT_TITLE : DOWN_TITLE;
  if (body)
    body.textContent =
      mode === 'maintenance' ? message || currentMessage || MAINT_BODY_DEFAULT : DOWN_BODY;
  if (crest) crest.textContent = mode === 'maintenance' ? '🛠' : '⚜';
}

function show(mode: Mode, message?: string): void {
  currentMode = mode;
  if (message) currentMessage = message;
  const el = ensureOverlay();
  paint(mode, message);
  el.style.display = 'flex';
  startPolling();
}

function hide(): void {
  currentMode = null;
  currentMessage = '';
  stopPolling();
  if (overlayEl) overlayEl.style.display = 'none';
}

function startPolling(): void {
  if (pollTimer !== null) return;
  pollTimer = window.setInterval(() => void checkHealth(), POLL_MS);
}

function stopPolling(): void {
  if (pollTimer !== null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}

async function checkHealth(): Promise<void> {
  try {
    const res = await fetch(`${BASE}/health`, { method: 'GET', cache: 'no-store' });
    if (!res.ok) {
      // Reachable but unhealthy — treat like a maintenance/down hold.
      return;
    }
    const data = (await res.json()) as { maintenance?: boolean };
    if (data.maintenance) {
      // Still in planned maintenance — make sure we're showing that mode.
      if (currentMode !== 'maintenance') paint('maintenance');
      currentMode = 'maintenance';
      return;
    }
    // Healthy AND not in maintenance → the realm is back. Reload to re-bootstrap.
    stopPolling();
    window.location.reload();
  } catch {
    // Still unreachable. If we were showing maintenance but can't even reach
    // health now, fall back to the generic "down" copy.
    if (currentMode !== 'down') paint('down');
    currentMode = 'down';
  }
}

/** The client couldn't reach the API at all. Show the down overlay + retry. */
export function notifyServerDown(): void {
  // Don't downgrade a more-specific maintenance notice to a generic outage.
  if (currentMode === 'maintenance') return;
  show('down');
}

/** The API answered with a 503 MAINTENANCE signal. Show the maintenance page. */
export function notifyMaintenance(message?: string): void {
  show('maintenance', message);
}

/** A request just succeeded — dismiss any stale overlay (e.g. transient blip). */
export function notifyServerOk(): void {
  if (currentMode !== null) hide();
}
